import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { SmileOutlined, CommentOutlined, EllipsisOutlined, EditOutlined, DeleteOutlined, ForwardOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';

const isDark = (theme) => theme?.colors?.background?.default === '#141414';

const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🔥', '✅', '❌'];

/**
 * Hover actions for a chat message: add reaction, reply (thread), more (edit / delete).
 * Matches backend: POST/DELETE reactions, PATCH/DELETE message, thread via parentMessageId.
 */
export function MessageActionsPanel({
    messageId,
    isOwn,
    onAddReaction,
    onReply,
    onEdit,
    onDelete,
    onForwardSelect,
    addReactionLoading,
}) {
    const theme = useTheme();
    const dark = isDark(theme);
    const [reactionOpen, setReactionOpen] = useState(false);

    const iconColor = dark ? 'rgba(255,255,255,0.65)' : '#666';
    const iconHover = dark ? '#fff' : '#111';
    const panelBg = dark ? '#2C2C2C' : '#ffffff';
    const panelBorder = dark ? '#434343' : '#e0e0e0';

    const btnBase = {
        width: 28,
        height: 28,
        border: 'none',
        borderRadius: 6,
        background: 'transparent',
        color: iconColor,
        cursor: onAddReaction || onReply || onEdit || onDelete || onForwardSelect ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
    };

    const reactionItems = REACTION_EMOJIS.map((emoji) => ({
        key: emoji,
        label: (
            <span
                style={{ fontSize: 18, cursor: 'pointer' }}
                onClick={() => {
                    onAddReaction?.({ messageId, emoji });
                    setReactionOpen(false);
                }}
            >
                {emoji}
            </span>
        ),
    }));

    const moreItems = [
        ...(onEdit && isOwn ? [{ key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: onEdit }] : []),
        ...(onDelete && isOwn
            ? [{ key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: onDelete }]
            : []),
    ].map(({ key, icon, label, danger, onClick }) => ({
        key,
        danger: !!danger,
        label: (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {icon}
                {label}
            </span>
        ),
        onClick,
    }));

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 6px',
                borderRadius: 10,
                background: panelBg,
                border: `1px solid ${panelBorder}`,
                boxShadow: dark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            }}
        >
            {onAddReaction && (
                <Dropdown
                    menu={{ items: reactionItems }}
                    trigger={['click']}
                    open={reactionOpen}
                    onOpenChange={setReactionOpen}
                    placement="topRight"
                    disabled={addReactionLoading}
                >
                    <button
                        type="button"
                        title="Add reaction"
                        style={btnBase}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = iconHover;
                            e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = iconColor;
                            e.currentTarget.style.background = 'transparent';
                        }}
                    >
                        <SmileOutlined />
                    </button>
                </Dropdown>
            )}

            {onReply && (
                <button
                    type="button"
                    title="Reply in thread"
                    style={btnBase}
                    onClick={onReply}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = iconHover;
                        e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = iconColor;
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <CommentOutlined />
                </button>
            )}

            {onForwardSelect && (
                <button
                    type="button"
                    title="Forward / select"
                    style={btnBase}
                    onClick={(e) => {
                        e.stopPropagation();
                        onForwardSelect(messageId);
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = iconHover;
                        e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = iconColor;
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <ForwardOutlined />
                </button>
            )}

            {onEdit && isOwn && (
                <button
                    type="button"
                    title="Edit message"
                    style={btnBase}
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = iconHover;
                        e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = iconColor;
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <EditOutlined style={{ fontSize: 15 }} />
                </button>
            )}

            {onDelete && isOwn && (
                <button
                    type="button"
                    title="Delete message"
                    style={{
                        ...btnBase,
                        marginLeft: 2,
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#ff4d4f';
                        e.currentTarget.style.background = 'rgba(255,77,79,0.1)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = iconColor;
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <DeleteOutlined style={{ fontSize: 15 }} />
                </button>
            )}
        </div>
    );
}
